/**
 * Settings Page
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  Info as InfoIcon,
  Build as BuildIcon,
  Article as ArticleIcon,
  ContactMail as ContactMailIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { PageHeader, RichTextEditor } from '../../components/common';
import {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  Settings,
} from '../../store/api/settingsApi';
import {
  useGetAboutQuery,
  useUpdateAboutMutation,
} from '../../store/api/aboutApi';
import {
  useGetContactQuery,
  useUpdateContactMutation,
  useGetContactMessagesQuery,
} from '../../store/api/contactApi';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const SettingsPage = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateSettingsMutation();
  
  // About the App
  const { data: aboutData, isLoading: isLoadingAbout } = useGetAboutQuery();
  const [updateAbout, { isLoading: isUpdatingAbout }] = useUpdateAboutMutation();
  
  // Contact Us
  const { data: contactData, isLoading: isLoadingContact } = useGetContactQuery();
  const [updateContact, { isLoading: isUpdatingContact }] = useUpdateContactMutation();
  
  const [formData, setFormData] = useState<Partial<Settings>>({});
  const [aboutFormData, setAboutFormData] = useState({ title: '', content: '' });
  const [contactFormData, setContactFormData] = useState({ 
    email: '', 
    phone: '', 
    address: '' 
  });

  const { data: messagesData, isLoading: isLoadingMessages } = useGetContactMessagesQuery();
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data?.data) {
      setFormData(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (aboutData?.data) {
      setAboutFormData({
        title: aboutData.data.title || '',
        content: aboutData.data.content || '',
      });
    }
  }, [aboutData]);

  useEffect(() => {
    if (contactData?.data) {
      setContactFormData({
        email: contactData.data.email || '',
        phone: contactData.data.phone || '',
        address: contactData.data.address || '',
      });
    }
  }, [contactData]);

  const handleInputChange = (field: keyof Settings, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.platform_fee_percentage !== undefined) {
      const fee = parseFloat(formData.platform_fee_percentage as any);
      if (isNaN(fee) || fee < 0 || fee > 100) {
        newErrors.platform_fee_percentage = t('settings.validation.deliveryFeeRange');
      }
    }

    if (formData.specialist_commission_percentage !== undefined) {
      const commission = parseFloat(formData.specialist_commission_percentage as any);
      if (isNaN(commission) || commission < 0 || commission > 100) {
        newErrors.specialist_commission_percentage = t('settings.validation.commissionRange');
      }
    }

    if (formData.otp_length !== undefined) {
      if (formData.otp_length < 4 || formData.otp_length > 10) {
        newErrors.otp_length = t('settings.validation.otpLengthRange');
      }
    }

    if (formData.email_port !== undefined) {
      if (formData.email_port < 1 || formData.email_port > 65535) {
        newErrors.email_port = t('settings.validation.emailPortRange');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('settings.toast.fixValidation'));
      return;
    }

    try {
      // Convert string percentages to numbers
      const updateData: any = { ...formData };
      if (updateData.platform_fee_percentage !== undefined) {
        updateData.platform_fee_percentage = parseFloat(updateData.platform_fee_percentage as any);
      }
      if (updateData.specialist_commission_percentage !== undefined) {
        updateData.specialist_commission_percentage = parseFloat(updateData.specialist_commission_percentage as any);
      }

      await updateSettings(updateData).unwrap();
      toast.success(t('settings.toast.updateSuccess'));
    } catch (error: any) {
      toast.error(error?.data?.message || t('settings.toast.updateError'));
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('settings.loadError')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.settings') },
        ]}
      />

      <form onSubmit={handleSubmit}>
        {/* Application Info */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                {t('settings.sections.appInfo')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('settings.form.appName')}
                  value={formData.app_name || ''}
                  onChange={(e) => handleInputChange('app_name', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('settings.form.appVersion')}
                  value={formData.app_version || ''}
                  onChange={(e) => handleInputChange('app_version', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('settings.form.supportEmail')}
                  type="email"
                  value={formData.support_email || ''}
                  onChange={(e) => handleInputChange('support_email', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('settings.form.supportPhone')}
                  value={formData.support_phone || ''}
                  onChange={(e) => handleInputChange('support_phone', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Maintenance Mode */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BuildIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                {t('settings.sections.maintenance')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.maintenance_mode || false}
                      onChange={(e) => handleInputChange('maintenance_mode', e.target.checked)}
                    />
                  }
                  label={t('settings.form.enableMaintenance')}
                />
              </Grid>
              {formData.maintenance_mode && (
                <>
                  <Grid size={{ xs: 12, sm: 12 }}>
                  <TextField
                    fullWidth
                    label={t('settings.form.maintenanceMessage')}
                    multiline
                    rows={4}
                    value={formData.maintenance_message || ''}
                    onChange={(e) => handleInputChange('maintenance_message', e.target.value)}
                    placeholder={t('settings.form.maintenanceMessagePlaceholder')}
                  />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* About the App */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                {t('settings.sections.about')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            {isLoadingAbout ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label={t('settings.form.title')}
                    value={aboutFormData.title}
                    onChange={(e) => setAboutFormData({ ...aboutFormData, title: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('settings.form.contentRichText')}
                  </Typography>
                  <RichTextEditor
                    value={aboutFormData.content}
                    onChange={(html) => setAboutFormData({ ...aboutFormData, content: html })}
                    placeholder={t('settings.form.aboutContentPlaceholder')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        await updateAbout(aboutFormData).unwrap();
                        toast.success(t('settings.toast.aboutUpdateSuccess'));
                      } catch (error: any) {
                        toast.error(error?.data?.message || t('settings.toast.aboutUpdateError'));
                      }
                    }}
                    disabled={isUpdatingAbout}
                    startIcon={isUpdatingAbout ? <CircularProgress size={20} /> : <SaveIcon />}
                  >
                    {isUpdatingAbout ? t('settings.buttons.saving') : t('settings.buttons.saveAbout')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Contact Us */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ContactMailIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                {t('settings.sections.contact')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            {isLoadingContact ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('settings.form.email')}
                    type="email"
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('settings.form.phone')}
                    value={contactFormData.phone}
                    onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label={t('settings.form.address')}
                    multiline
                    rows={3}
                    value={contactFormData.address}
                    onChange={(e) => setContactFormData({ ...contactFormData, address: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        await updateContact(contactFormData).unwrap();
                        toast.success(t('settings.toast.contactUpdateSuccess'));
                      } catch (error: any) {
                        toast.error(error?.data?.message || t('settings.toast.contactUpdateError'));
                      }
                    }}
                    disabled={isUpdatingContact}
                    startIcon={isUpdatingContact ? <CircularProgress size={20} /> : <SaveIcon />}
                  >
                    {isUpdatingContact ? t('settings.buttons.saving') : t('settings.buttons.saveContact')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Contact Us Messages (from clients) */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <MessageIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                {t('settings.sections.contactMessages')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            {isLoadingMessages ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('settings.cols.date')}</strong></TableCell>
                      <TableCell><strong>{t('settings.cols.name')}</strong></TableCell>
                      <TableCell><strong>{t('settings.cols.email')}</strong></TableCell>
                      <TableCell><strong>{t('settings.cols.message')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {messagesData?.data?.length ? (
                      messagesData.data.map((msg) => (
                        <TableRow key={msg.id}>
                          <TableCell>
                            {msg.created_at ? new Date(msg.created_at).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell>{msg.full_name}</TableCell>
                          <TableCell>{msg.email}</TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>{msg.message}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          {t('settings.empty')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={isUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={isUpdating}
            size="large"
          >
            {isUpdating ? t('settings.buttons.saving') : t('settings.buttons.saveSettings')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default SettingsPage;

